import React, { useState } from "react";
import { Trophy, TrendingDown, HelpCircle, MinusCircle, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { updateOutcome } from "@/lib/api";
import { fmtEUR, fmtDate } from "@/lib/format";

const STATUSES = [
    { key: "pending", label: "Pendiente", color: "slate", icon: HelpCircle },
    { key: "won", label: "Ganada", color: "emerald", icon: Trophy },
    { key: "lost", label: "Perdida", color: "red", icon: TrendingDown },
    { key: "desierta", label: "Desierta", color: "amber", icon: MinusCircle },
    { key: "withdrawn", label: "Retirada", color: "slate", icon: MinusCircle },
];

export const OutcomePanel = ({ tender, onChange }) => {
    const initial = tender.outcome || { status: "pending" };
    const [form, setForm] = useState({
        status: initial.status || "pending",
        outcome_date: initial.outcome_date || "",
        our_offer_amount: initial.our_offer_amount ?? "",
        awarded_amount: initial.awarded_amount ?? "",
        winning_competitor: initial.winning_competitor || "",
        lessons_learned: initial.lessons_learned || "",
    });
    const [saving, setSaving] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                ...form,
                our_offer_amount:
                    form.our_offer_amount === "" ? null : Number(form.our_offer_amount),
                awarded_amount:
                    form.awarded_amount === "" ? null : Number(form.awarded_amount),
                outcome_date: form.outcome_date || null,
            };
            const updated = await updateOutcome(tender.id, body);
            onChange?.(updated);
            toast.success("Resultado actualizado");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="outcome-panel">
            <div>
                <h2 className="font-display text-2xl font-medium">Resultado de la licitación</h2>
                <p className="text-sm text-slate-500 mt-1">
                    Registra la adjudicación para alimentar tus analíticas y benchmarking por CPV.
                </p>
            </div>

            {/* Status selector */}
            <div className="bg-white border border-slate-200 p-5">
                <div className="overline mb-3">Estado</div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {STATUSES.map((s) => {
                        const active = form.status === s.key;
                        return (
                            <button
                                key={s.key}
                                type="button"
                                onClick={() => update("status", s.key)}
                                className={`flex items-center gap-2 px-3 h-11 border rounded-xl text-sm font-medium transition-colors ${
                                    active
                                        ? s.color === "emerald"
                                            ? "bg-emerald-600 border-emerald-600 text-white"
                                            : s.color === "red"
                                              ? "bg-red-600 border-red-600 text-white"
                                              : s.color === "amber"
                                                ? "bg-amber-500 border-amber-500 text-white"
                                                : "bg-[#0055ff] border-[#0055ff] text-white"
                                        : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                                }`}
                                data-testid={`outcome-status-${s.key}`}
                            >
                                <s.icon size={14} />
                                {s.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Details form */}
            <div className="bg-white border border-slate-200 p-6 space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Fecha del resultado">
                        <Input
                            type="date"
                            value={form.outcome_date}
                            onChange={(e) => update("outcome_date", e.target.value)}
                            className="rounded-xl border-slate-300"
                            data-testid="input-outcome-date"
                        />
                    </Field>
                    <Field label="Nuestra oferta (€)">
                        <Input
                            type="number"
                            step="0.01"
                            value={form.our_offer_amount}
                            onChange={(e) => update("our_offer_amount", e.target.value)}
                            className="rounded-xl border-slate-300 font-mono"
                            placeholder="115000"
                            data-testid="input-our-offer"
                        />
                    </Field>
                </div>
                {form.status === "won" && (
                    <Field label="Importe adjudicación (€)">
                        <Input
                            type="number"
                            step="0.01"
                            value={form.awarded_amount}
                            onChange={(e) => update("awarded_amount", e.target.value)}
                            className="rounded-xl border-slate-300 font-mono"
                            placeholder="112500"
                            data-testid="input-awarded-amount"
                        />
                    </Field>
                )}
                {form.status === "lost" && (
                    <>
                        <Field label="Competidor ganador">
                            <Input
                                value={form.winning_competitor}
                                onChange={(e) => update("winning_competitor", e.target.value)}
                                className="rounded-xl border-slate-300"
                                placeholder="Nombre del adjudicatario"
                                data-testid="input-winner"
                            />
                        </Field>
                        <Field label="Importe adjudicación competidor (€)">
                            <Input
                                type="number"
                                step="0.01"
                                value={form.awarded_amount}
                                onChange={(e) => update("awarded_amount", e.target.value)}
                                className="rounded-xl border-slate-300 font-mono"
                                placeholder="108000"
                            />
                        </Field>
                    </>
                )}
                <Field label="Lecciones aprendidas">
                    <Textarea
                        value={form.lessons_learned}
                        onChange={(e) => update("lessons_learned", e.target.value)}
                        className="rounded-xl border-slate-300"
                        placeholder="Qué funcionó, qué mejorar para próximas ofertas…"
                        rows={4}
                        data-testid="input-lessons"
                    />
                </Field>

                <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                    <div className="text-xs text-slate-500">
                        {tender.outcome?.status && tender.outcome.status !== "pending" && (
                            <>
                                Última actualización: <strong>{STATUSES.find((s) => s.key === tender.outcome.status)?.label}</strong>
                                {tender.outcome.outcome_date && ` · ${fmtDate(tender.outcome.outcome_date)}`}
                                {tender.outcome.awarded_amount != null && ` · ${fmtEUR(tender.outcome.awarded_amount)}`}
                            </>
                        )}
                    </div>
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="rounded-xl bg-[#0055ff] hover:bg-[#0044cc] text-white"
                        data-testid="save-outcome-btn"
                    >
                        {saving ? "Guardando…" : (
                            <>
                                <Save size={14} className="mr-1.5" />
                                Guardar resultado
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
};

const Field = ({ label, children }) => (
    <div>
        <Label className="overline block mb-1.5 !text-slate-400">{label}</Label>
        {children}
    </div>
);

export default OutcomePanel;
